import { useMutation, useQueryClient } from '@tanstack/react-query';
import { USER_ROLE } from 'khufu-legos';
import { MouseEventHandler, useEffect } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

import { updateUser } from '../../../apis/queries';
import SubmitButton from '../../../components/Buttons/SubmitButton';
import { isValidPhoneNumber } from '../../../helpers/validation';
import { User } from '../../../types';
import DisplayFieldErrors from '../../../utils/DisplayFormError';
import { formatDateTime } from '../../../utils/formatDateTime';
import { notification, NotificationKinds } from '../../../widgets/Notifications/notifications';

interface UpdateUserFormInput {
  email: string;
  password: string;
  nickName: string;
  fullName: string;
  phoneNumber: string;
  role: USER_ROLE;
  isActive: boolean;
}

const UserDetailForm = ({
  user,
  isEditMode,
  onEditFormSubmit
}: {
  user: User;
  isEditMode: boolean;
  isEditFormSubmitting: boolean;
  onEditFormSubmit: (value: boolean) => void;
}) => {
  const { email, fullName, nickName, phoneNumber, isActive, role, _id, createdAt, updatedAt } = user;
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (user: UpdateUserFormInput) => {
      return updateUser(String(_id), {
        fullName: user.fullName,
        email: user.email,
        nickName: user.nickName,
        phoneNumber: user.phoneNumber,
        role: user.role,
        isActive: user.isActive
      });
    },
    onSuccess: data => {
      const { data: updatedUser } = data;
      notification({
        variant: NotificationKinds.SUCCESS,
        title: `USER UPDATED`,
        subtitle: `${updatedUser?.fullName} has been updated`
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['users', { id: _id }] });
    }
  });

  const loading = mutation?.isPending;

  // const createdBy = user.createdBy.fullName ?? '';

  useEffect(() => {
    onEditFormSubmit(loading);
  }, [loading]);

  const methods = useForm<UpdateUserFormInput>({
    mode: 'onBlur',
    defaultValues: {
      fullName,
      phoneNumber,
      nickName,
      email,
      isActive,
      role
    }
  });

  const {
    register,
    formState: { isValid, errors, isDirty },
    reset,
    getValues
  } = methods;

  const onUpdateUser: MouseEventHandler<HTMLButtonElement> = event => {
    event.preventDefault();
    const fields = getValues();
    mutation?.mutate(fields);
  };

  const disabled = loading || !isEditMode;

  const handleCancel = () => {
    reset();
  };

  return (
    <Form>
      <Form.Group className="my-4">
        <Form.Check className="fw-bold" type="switch" label={'Activate User'} {...register('isActive', { disabled })} />
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label>Email</Form.Label>
        <Form.Control type="email" placeholder="Updated At" disabled={true} value={email} />
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label>Fullname</Form.Label>
        <Form.Control
          type="text"
          placeholder="John Doe"
          {...register('fullName', {
            required: 'Name is required',
            validate: value => value.trim() !== '' || 'Full name cannot be empty',
            pattern: {
              value: /^[a-zA-Z]+( [a-zA-Z]+)*$/,
              message: 'Full name should contain only alphabets'
            },
            disabled,
            minLength: { value: 3, message: 'Name must be atleast 3 characters' }
          })}
          onInput={e => {
            e.currentTarget.value = e.currentTarget.value.replace(/[^a-zA-Z\s]/g, '');
          }}
        />
        {errors?.fullName && <DisplayFieldErrors displayText={errors.fullName.message!} />}
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label>Phone number</Form.Label>
        <Form.Control
          type="text"
          placeholder="08039283847"
          {...register('phoneNumber', {
            required: 'Phone number is required',
            pattern: {
              value: /^[0-9]+$/,
              message: 'Phone number must contain only digits'
            },
            validate: {
              isNotEmpty: value => value.trim() !== '' || 'Phone number cannot be empty',
              isValidFormat: isValidPhoneNumber
            },
            disabled
          })}
          onInput={e => {
            e.currentTarget.value = e.currentTarget.value.replace(/\D/g, '');
          }}
        />
        {errors?.phoneNumber && <DisplayFieldErrors displayText={errors.phoneNumber.message!} />}
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label>Nickname</Form.Label>
        <Form.Control
          type="text"
          placeholder="John Doe"
          {...register('nickName', {
            disabled,
            minLength: { value: 3, message: 'Name must be atleast 3 characters' },
            validate: value => value.trim() !== '' || 'Nickname cannot be empty',
            pattern: {
              value: /^[a-zA-Z]+$/,
              message: 'Nickname should contain only alphabets'
            }
          })}
          onInput={e => {
            e.currentTarget.value = e.currentTarget.value.replace(/[^a-zA-Z\s]/g, '');
          }}
        />
        {errors?.nickName && <DisplayFieldErrors displayText={errors.nickName.message!} />}
      </Form.Group>
      <Form.Group>
        <Form.Label htmlFor="role">Role</Form.Label>
        <Form.Select id="role" {...register('role', { required: true, disabled })}>
          <option value="admin">Admin</option>
          <option value="finance">Finance</option>
          <option value="supervisor">Supervisor</option>
          <option value="stockKeeper">Stock Keeper</option>
        </Form.Select>
      </Form.Group>
      {/* <Form.Group className="my-3">
        <Form.Label>Created By</Form.Label>
        <Form.Control type="text" placeholder="Created By" disabled={true} value={createdBy.toString()} />
      </Form.Group> */}
      <Form.Group className="my-3">
        <Form.Label>Created At</Form.Label>
        <Form.Control type="text" placeholder="Created At" disabled={true} value={formatDateTime(createdAt)} />
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label>Last Updated</Form.Label>
        <Form.Control type="text" placeholder="Updated At" disabled={true} value={formatDateTime(updatedAt)} />
      </Form.Group>

      {isEditMode && (
        <Row className="my-5">
          <Col>
            <SubmitButton
              type="button"
              buttonText="cancel"
              variant="white"
              onClick={handleCancel}
              disabled={disabled}
              loading={false}
            />
          </Col>
          <Col>
            <SubmitButton
              type="submit"
              buttonText="Submit"
              onClick={onUpdateUser}
              loading={loading}
              disabled={!isDirty || !isValid || disabled}
            />
          </Col>
        </Row>
      )}
    </Form>
  );
};

export default UserDetailForm;
